import { useMutation } from '@apollo/client/react';
import { Alert, Button } from 'antd';
import { forEach, keys } from 'lodash';
import React, { useCallback, useRef, useState } from 'react';
import { toast } from '../../../../../../../../apollo';
import {
  BULK_ACTION,
  BULK_JOB_STATUS,
} from '../../../../../../../../common/constants';
import CommonBulkConfirmationModal from '../../../../../../../../components/CommonBulkConfirmationModal';
import CommonDrawer from '../../../../../../../../components/CommonDrawer';
import { BULK_REMOVE_PROJECT_EQC_TYPE_STAGE_APPROVERS } from '../../../../../../graphql/Mutation';
import RemoveApproversStep from './RemoveApproversStep';

const BulkRemoveApproversDrawer = ({
  showDrawer,
  setShowDrawer,
  selectedRows,
  isSelectAllAcrossPages,
  exceptIds,
  paginationProp,
  prefetchedStageData,
  getBulkProjectStageJobStatus,
  startJobTracking,
  onSuccess,
  projectEqcTypeId,
}) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [loading, setLoading] = useState(false);

  const removalsRef = useRef({});
  const enabledLevelsRef = useRef({});
  const rfiRemovalsRef = useRef({});
  const rfiEnabledLevelsRef = useRef({});
  const hasRemoveValidationError = useRef(false);

  const selectedCount = isSelectAllAcrossPages
    ? (paginationProp?.total || 0) - (exceptIds?.length || 0)
    : selectedRows?.length || 0;

  const handleRemovalsChange = useCallback((newRemovals) => {
    removalsRef.current = newRemovals;
  }, []);

  const handleEnabledLevelsChange = useCallback((newEnabledLevels) => {
    enabledLevelsRef.current = newEnabledLevels;
  }, []);

  const handleRfiRemovalsChange = useCallback((newRfiRemovals) => {
    rfiRemovalsRef.current = newRfiRemovals;
  }, []);

  const handleRfiEnabledLevelsChange = useCallback((newRfiEnabledLevels) => {
    rfiEnabledLevelsRef.current = newRfiEnabledLevels;
  }, []);

  const handleValidationErrorChange = useCallback((hasError) => {
    hasRemoveValidationError.current = hasError;
  }, []);

  const [bulkRemoveApprovers] = useMutation(
    BULK_REMOVE_PROJECT_EQC_TYPE_STAGE_APPROVERS,
    {
      onCompleted(response) {
        setLoading(false);
        const { status, jobId } =
          response?.bulkRemoveProjectEqcTypeStageApprovers || {};

        if (status === BULK_JOB_STATUS.SUCCESS && jobId) {
          startJobTracking(jobId, BULK_ACTION.EDIT_APPROVERS);
          getBulkProjectStageJobStatus({
            variables: { jobId },
          });
        }

        onSuccess?.();
        setShowDrawer(false);
      },
      onError(error) {
        setLoading(false);
        setApiError(error?.message);
      },
    },
  );

  const buildStageRemovals = () => {
    const currentRemovals = removalsRef.current || {};
    const {
      enabledLevels: currentEnabledLevels,
      levelNames: currentLevelNames,
    } = enabledLevelsRef.current || {};
    const stageRemovalsMap = {};

    const enabledLevelNumbers = keys(currentEnabledLevels || {})
      ?.map(Number)
      ?.filter((lvl) => currentEnabledLevels?.[lvl] !== false)
      ?.sort((a, b) => a - b);

    // Helper: check if all higher enabled levels for a stage have all approvers removed
    const isEffectiveHighest = (stageId, levelNumber) => {
      const stageInfo = prefetchedStageData?.find(
        (stage) => String(stage?.id) === String(stageId),
      );
      const higherLevels = enabledLevelNumbers?.filter(
        (lvl) => lvl > levelNumber,
      );
      return higherLevels?.every((lvl) => {
        const lvlInfo = stageInfo?.levels?.find(
          (level) => level?.levelNumber === lvl,
        );
        const totalUsers =
          (lvlInfo?.internalUsers?.length || 0) +
          (lvlInfo?.externalUsers?.length || 0);
        if (totalUsers === 0) return true;
        const lvlRemovals = currentRemovals?.[stageId]?.[lvl];
        if (!lvlRemovals) return false;
        const removedCount =
          (lvlRemovals?.internalIds?.length || 0) +
          (lvlRemovals?.externalIds?.length || 0);
        return removedCount >= totalUsers;
      });
    };

    // Step 1: Process individual cell removals (specific approver removals)
    forEach(keys(currentRemovals), (stageId) => {
      const stageLevels = currentRemovals[stageId] || {};
      forEach(keys(stageLevels), (levelKey) => {
        const levelNumber = Number(levelKey);
        const cellRemovals = stageLevels[levelKey];
        const internalIds = cellRemovals?.internalIds || [];
        const externalIds = cellRemovals?.externalIds || [];
        if (!internalIds?.length && !externalIds?.length) return;

        // Skip if level is toggled off (handled as removeLevel below)
        if (currentEnabledLevels?.[levelNumber] === false) return;

        // Check if all approvers are removed for this cell
        const stageInfo = prefetchedStageData?.find(
          (stage) => String(stage?.id) === String(stageId),
        );
        const levelInfo = stageInfo?.levels?.find(
          (level) => level?.levelNumber === levelNumber,
        );
        const totalApprovers =
          (levelInfo?.internalUsers?.length || 0) +
          (levelInfo?.externalUsers?.length || 0);
        const totalRemoved = internalIds?.length + externalIds?.length;
        const isAllRemoved = totalRemoved >= totalApprovers;

        if (!stageRemovalsMap[stageId]) {
          stageRemovalsMap[stageId] = [];
        }

        // If all approvers removed and effectively highest, send removeLevel: true
        if (isAllRemoved && isEffectiveHighest(stageId, levelNumber)) {
          stageRemovalsMap[stageId].push({
            levelNumber,
            levelName: cellRemovals?.levelName || `Level ${levelNumber}`,
            removeLevel: true,
          });
        } else {
          stageRemovalsMap[stageId].push({
            levelNumber,
            levelName: cellRemovals?.levelName || `Level ${levelNumber}`,
            removeInternalApproverIds: internalIds,
            removeExternalUserApproverIds: externalIds,
          });
        }
      });
    });

    // Step 2: Handle toggled-off levels (removeLevel: true for all stages)
    forEach(keys(currentEnabledLevels || {}), (levelKey) => {
      const levelNumber = Number(levelKey);
      if (currentEnabledLevels?.[levelNumber] === false) {
        // Get all stage IDs from prefetchedStageData
        const stageIds =
          prefetchedStageData
            ?.filter((stage) =>
              stage?.levels?.some(
                (level) => level?.levelNumber === levelNumber,
              ),
            )
            ?.map((stage) => stage?.id) || [];

        stageIds?.forEach((stageId) => {
          if (!stageRemovalsMap[stageId]) {
            stageRemovalsMap[stageId] = [];
          }
          stageRemovalsMap[stageId].push({
            levelNumber,
            levelName:
              currentLevelNames?.[levelNumber] || `Level ${levelNumber}`,
            removeLevel: true,
          });
        });
      }
    });

    return Object?.keys(stageRemovalsMap)?.map((stageId) => {
      const levelData = stageRemovalsMap[stageId];
      // Get level numbers that exist for this specific stage
      const stageInfo = prefetchedStageData?.find(
        (stage) => String(stage?.id) === String(stageId),
      );
      const stageLevelNumbers =
        stageInfo?.levels?.map((level) => level?.levelNumber) || [];
      // Check if all levels for this stage have removeLevel: true
      const allLevelsRemoved =
        stageLevelNumbers?.length > 0 &&
        stageLevelNumbers?.every((lvl) =>
          levelData?.some(
            (entry) => entry?.levelNumber === lvl && entry?.removeLevel,
          ),
        );
      const result = {
        projectEqcTypeStageId: stageId,
        approvalLevelData: levelData,
      };
      if (allLevelsRemoved) {
        result.needApproval = false;
      }
      return result;
    });
  };

  const buildRfiStageRemovals = () => {
    const currentRfiRemovals = rfiRemovalsRef.current || {};
    const currentRfiEnabledLevels = rfiEnabledLevelsRef.current || {};
    const rfiStageRemovalsMap = {};

    // Step 1: Process individual RFI cell removals
    forEach(keys(currentRfiRemovals), (stageId) => {
      const stageLevels = currentRfiRemovals[stageId] || {};
      forEach(keys(stageLevels), (levelKey) => {
        const levelNumber = Number(levelKey);
        const cellRemovals = stageLevels[levelKey];
        const internalIds = cellRemovals?.internalIds || [];
        const externalIds = cellRemovals?.externalIds || [];
        if (!internalIds?.length && !externalIds?.length) return;

        if (currentRfiEnabledLevels?.[levelNumber] === false) return;

        if (!rfiStageRemovalsMap[stageId]) {
          rfiStageRemovalsMap[stageId] = [];
        }

        rfiStageRemovalsMap[stageId]?.push({
          levelNumber,
          levelName: cellRemovals?.levelName || 'RFI Approver',
          removeInternalApproverIds: internalIds,
          removeExternalUserApproverIds: externalIds,
        });
      });
    });

    // Step 2: Handle toggled-off RFI levels
    forEach(keys(currentRfiEnabledLevels), (levelKey) => {
      const levelNumber = Number(levelKey);
      if (currentRfiEnabledLevels?.[levelNumber] === false) {
        const rfiStageIds =
          prefetchedStageData
            ?.filter(
              (stage) =>
                stage?.isRfiActive &&
                stage?.rfiLevels?.some(
                  (level) => level?.levelNumber === levelNumber,
                ),
            )
            ?.map((stage) => stage?.rfiStageId || stage?.id) || [];

        rfiStageIds?.forEach((stageId) => {
          if (!rfiStageRemovalsMap[stageId]) {
            rfiStageRemovalsMap[stageId] = [];
          }
          rfiStageRemovalsMap[stageId].push({
            levelNumber,
            levelName: 'RFI Approver',
            removeLevel: true,
          });
        });
      }
    });

    return Object?.keys(rfiStageRemovalsMap)?.map((stageId) => {
      const levelData = rfiStageRemovalsMap[stageId];
      // Get RFI level numbers that exist for this specific stage
      const stageInfo = prefetchedStageData?.find(
        (stage) => String(stage?.rfiStageId || stage?.id) === String(stageId),
      );
      const rfiStageLevelNumbers =
        stageInfo?.rfiLevels?.map((level) => level?.levelNumber) || [];
      const allRfiLevelsRemoved =
        rfiStageLevelNumbers?.length > 0 &&
        rfiStageLevelNumbers?.every((lvl) => {
          const entry = levelData?.find(
            (levelEntry) => levelEntry?.levelNumber === lvl,
          );
          if (!entry) return false;
          if (entry?.removeLevel) return true;
          const rfiLevelInfo = stageInfo?.rfiLevels?.find(
            (level) => level?.levelNumber === lvl,
          );
          const totalApprovers =
            (rfiLevelInfo?.internalUsers?.length || 0) +
            (rfiLevelInfo?.externalUsers?.length || 0);
          const totalRemoved =
            (entry?.removeInternalApproverIds?.length || 0) +
            (entry?.removeExternalUserApproverIds?.length || 0);
          return totalApprovers > 0 && totalRemoved >= totalApprovers;
        });
      const result = {
        projectEqcTypeStageId: stageId,
        approvalLevelData: levelData,
      };
      if (allRfiLevelsRemoved) {
        result.needApproval = false;
      }
      return result;
    });
  };

  const onSubmit = async () => {
    try {
      setApiError(null);

      if (hasRemoveValidationError.current) {
        toast({
          message: 'Please fix validation errors before saving',
          type: 'error',
        });
        return;
      }

      const stageRemovals = buildStageRemovals();
      const rfiStageRemovals = buildRfiStageRemovals();

      if (!stageRemovals?.length && !rfiStageRemovals?.length) {
        toast({
          message: 'No updates made. Please make changes to save.',
          type: 'error',
        });
        return;
      }

      const mutationData = {
        projectEqcTypeId,
      };

      if (stageRemovals?.length > 0) {
        mutationData.stageRemovals = stageRemovals;
      }

      if (rfiStageRemovals?.length > 0) {
        mutationData.rfiStageRemovals = rfiStageRemovals;
      }

      setLoading(true);
      await bulkRemoveApprovers({
        variables: {
          data: mutationData,
        },
      });
    } catch (error) {
      setLoading(false);
      return error;
    }
  };

  const handleCancel = () => {
    const hasRemovals = Object?.values(removalsRef.current || {})?.some(
      (stageLevels) =>
        Object?.values(stageLevels || {})?.some(
          (cell) =>
            cell?.internalIds?.length > 0 || cell?.externalIds?.length > 0,
        ),
    );
    const hasRfiRemovals = Object.values(rfiRemovalsRef.current || {})?.some(
      (stageLevels) =>
        Object?.values(stageLevels || {})?.some(
          (cell) =>
            cell?.internalIds?.length > 0 || cell?.externalIds?.length > 0,
        ),
    );
    const hasLevelToggles = Object?.values(
      enabledLevelsRef?.current?.enabledLevels || {},
    )?.some((enabled) => enabled === false);
    const hasRfiLevelToggles = Object?.values(
      rfiEnabledLevelsRef?.current || {},
    )?.some((enabled) => enabled === false);

    if (
      hasRemovals ||
      hasRfiRemovals ||
      hasLevelToggles ||
      hasRfiLevelToggles
    ) {
      setShowConfirmationModal(true);
    } else {
      setShowDrawer(false);
    }
  };

  const handleConfirmDiscard = () => {
    setShowDrawer(false);
  };

  return (
    <>
      <CommonDrawer
        title={`Bulk Remove Approvers (${selectedCount} Stages Selected)`}
        open={showDrawer}
        onClose={handleCancel}
        width={1000}
        getContainer={() =>
          // eslint-disable-next-line no-undef
          document.getElementById('stage-list-container') || document.body
        }
      >
        <div className="bulk-approvers-step add-approver-modal">
          <RemoveApproversStep
            projectEqcTypeId={projectEqcTypeId}
            isSelectAllAcrossPages={isSelectAllAcrossPages}
            exceptIds={exceptIds}
            prefetchedStageData={prefetchedStageData}
            onRemovalsChange={handleRemovalsChange}
            onEnabledLevelsChange={handleEnabledLevelsChange}
            onRfiRemovalsChange={handleRfiRemovalsChange}
            onRfiEnabledLevelsChange={handleRfiEnabledLevelsChange}
            onValidationErrorChange={handleValidationErrorChange}
          />
        </div>
        {apiError && (
          <Alert
            title={apiError}
            type="error"
            showIcon
            closable={{ closeIcon: true, onClose: () => setApiError(null) }}
            className="mb-16"
          />
        )}
        <div className="form-buttons">
          <Button
            shape="round"
            className="cancel-button"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            shape="round"
            type="primary"
            className="save-button"
            loading={loading}
            onClick={onSubmit}
          >
            Save
          </Button>
        </div>
      </CommonDrawer>
      {showConfirmationModal && (
        <CommonBulkConfirmationModal
          showModal={showConfirmationModal}
          setShowModal={setShowConfirmationModal}
          onConfirm={handleConfirmDiscard}
          title="Unsaved Changes"
          message="Your changes are not saved. Do you want to discard them?"
        />
      )}
    </>
  );
};

export default BulkRemoveApproversDrawer;
